import "./TargetsTactics.scss";
import "../../components/BriefingDocument/BriefingDocument.scss";
import { useState, useEffect, useMemo, useCallback } from "react";
import NegotiationTable from "../NegotiationTable/NegotiationTable";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputSwitch } from "primereact/inputswitch";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useTenderSuppliers } from "../../hooks/useTenderSuppliers";
import targetANDtacticsStore from "../../stores/targetANDtacticsStore";
import { useNegotiationsStore } from "../../stores/negotiationsStore";
import { useNavigationContextStore } from "../../stores/navigationContextStore";
import { Card } from "primereact/card";
import { useParams, useLocation } from "react-router-dom";
import { apolloClient } from "../../lib/apollo";
import { useChatStore } from "../../stores/chatStore";
import CustomButton from "../Common/CustomButton/CustomButton";
import RegeneratedTargets from "./RegeneratedTargets";
import CustomDropdown from "../Common/CustomDropdown/CustomDropdown";
import { useToastStore } from "../../stores/toastStore";
import DisplayGroupAccordion from "./DisplayGroupAccordion";

interface TabItem {
    primaryText: string;
    key: string;
    value: string;
    key2: string;
    value2: string;
}

const headers = [
    { key: "parameter", label: "Parameter", isLeftColumn: true },
    { key: "objective", label: "Objectives" },
    { key: "impact", label: "Impact" },
    { key: "info", label: "" },
];

// Function to format parameter names
const formatParameterName = (parameterName: string): string => {
    const parameterMap: { [key: string]: string } = {
        'cost_commercial': 'Cost',
        'service': 'Service',
        'quality': 'Quality',
        'innovation': 'Innovation',
        'assurance_of_supply': 'Supply Assurance',
        'regulatory': 'Regulatory'
    };

    return parameterMap[parameterName] || parameterName;
};

const concessionHeaders = [
    { key: "parameter", label: "Negotiation Parameters", isLeftColumn: true },
    { key: "objective", label: "Objectives" },
];

type TargetHeading = {
    product_description: string;
    bid_id: number;
    tender_id: number;
    tender_name?: string;
    supplier_bid?: number | null;
    currency?: string;
    target_price_reasoning?: string;
};

type SummaryEntry = {
    product_name: string;
    buyer_targets?: { target_price: number | null; supplier_bid: number | null };
    target_pricing?: {
        opening_target?: string | number | null;
        internal_target?: string | number | null;
        supplier_target?: number | null;
    };
    target_allocation?: {
        supplier_volume_allocation?: string | null;
        opening_volume_allocation?: string | null;
    };
    category_goals?:
    | string
    | Record<string, unknown>
    | {
        parameter_name?: string;
        objective?: string;
        rationale?: string;
        leverage_level?: string;
    }[];
    mdo?: { target_price: number | null; supplier_bid: number | null };
    price_laa?: { target_price: number | null; supplier_bid: number | null };
    batna?: string | null;
    llm_reasoning?: string | null;
};

type TargetCard = {
    label: string;
    subLabel?: string;
    value?: string | null;
    supplierBid?: string | null;
    bullets?: string[];
    isBatna?: boolean;
    type?: "target_pricing";
    pricing?: {
        opening: string | number | null;
        internal: string | number | null;
        supplier: string | number | null;
    };
    reasoning?: string | null;
};
const TargetsTactics = () => {
    const { negotiation_id } = useParams<{ negotiation_id?: string }>();
    const location = useLocation();
    const negId = negotiation_id ? parseInt(negotiation_id, 10) : undefined;
    const [_kpis, setKpis] = useState<TabItem[]>([]);
    const [selectedSupplier, setSelectedSupplier] = useState<any>(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [modalContent, setModalContent] = useState<{
        targetPriceReasoning?: string;
        llmReasoning?: string;
        powerDynamicsAssessment?: Array<{
            id: number;
            party: string;
            question: string;
            answer?: string;
            reasoning?: string;
            score?: number;
        }>;
        buyerPowerScore?: number;
        supplierPowerScore?: number;
        powerDifferential?: number;
    }>({});
    const { isOpen } = useChatStore();
    const [hasModifiedWelcome, setHasModifiedWelcome] = useState(false);
    const [isRegenerated, setIsRegenerated] = useState(false);
    const [bdTargets, setBdTargets] = useState<TargetHeading[]>([]);
    const [bdSummary, setBdSummary] = useState<SummaryEntry[]>([]);
    const [loadingTargets, _setLoadingTargets] = useState(false);
    const [loadingSummary, setLoadingSummary] = useState(false);
    const { error: _error } = useToastStore.getState();

    // Store hooks
    const {
        tenders,
        selectedTender,
        setSelectedTender
    } = useNegotiationsStore();
    const {
        selectedTenderId,
        selectedSupplierId,
        supplierTabIndex,
        setSelectedTenderId,
        setSelectedSupplierId,
        setSupplierTabIndex,
        updateURL
    } = useNavigationContextStore();
    const {
        strategy,
        targets,
        tactics,
        fetchStrategy,
        fetchTargets,
        fetchTactics,
        fetchAlternatives,
        scriptStatus,
        getScriptStatus,
        regenerateScript,
        regeneratedTableData,
        stopStrategySubscription,
        createScriptLoading,
        groupByDisplay,
        setGroupByDisplay
    } = targetANDtacticsStore();

    // Use negotiation ID for fetching suppliers (across all tenders)
    const { suppliers, error: suppliersError } = useTenderSuppliers(
        negId ? { negotiation_id: negId } : {}
    );

    const formatCurrency = (val: number | null | undefined) => {
        if (val === null || val === undefined) return null;
        return `EUR ${val.toFixed(4)}`;
    };

    const normalizeProductKey = (name?: string | null) => (name || "").trim().toLowerCase();

    // Memoize dropdown options to prevent infinite re-renders
    const supplierOptions = useMemo(() =>
        suppliers.map((s) => ({
            name: s.supplier_name,
            code: s.nsp_supplier_id?.toString() || ''
        })),
        [suppliers]
    );

    const selectedSupplierOption = useMemo(() => {
        if (!selectedSupplier?.nsp_supplier_id) return null;
        return supplierOptions.find(
            (opt) => opt.code === selectedSupplier.nsp_supplier_id?.toString()
        ) || null;
    }, [selectedSupplier?.nsp_supplier_id, supplierOptions]);

    // Removed displayGroups logic - using flat sourcingProducts structure

    const buildSummaryCards = useCallback((entry?: SummaryEntry | null) => {
        if (!entry) return [];
        const cards: any[] = [];

        // Always show Target Pricing if entry exists
        if (entry.target_pricing || entry) {
            const openingVal =
                typeof entry.target_pricing?.opening_target === "number"
                    ? formatCurrency(entry.target_pricing.opening_target)
                    : entry.target_pricing?.opening_target ?? "-";
            const internalVal =
                typeof entry.target_pricing?.internal_target === "number"
                    ? formatCurrency(entry.target_pricing.internal_target)
                    : entry.target_pricing?.internal_target ?? "-";
            const supplierVal =
                typeof entry.target_pricing?.supplier_target === "number"
                    ? formatCurrency(entry.target_pricing.supplier_target)
                    : entry.target_pricing?.supplier_target ?? "-";
            cards.push({
                label: "Target Pricing",
                type: "target_pricing",
                pricing: {
                    opening: openingVal,
                    internal: internalVal,
                    supplier: supplierVal,
                }
            });
        }

        // Buyer Targets - hidden per request
        // cards.push({
        //     label: "Buyer Targets",
        //     subLabel: "Target Price",
        //     value: formatCurrency(entry.buyer_targets?.target_price) ?? "-",
        //     supplierBid: formatCurrency(entry.buyer_targets?.supplier_bid) ?? "-",
        // });

        cards.push({
            label: "MDO",
            subLabel: "(Most Desired Outcome)",
            value: formatCurrency(entry.mdo?.target_price) ?? "-",
            supplierBid: formatCurrency(entry.mdo?.supplier_bid) ?? "-",
        });

        cards.push({
            label: "Supplier Target",
            value: formatCurrency(entry.target_pricing?.supplier_target) ?? "-",
            reasoning: entry.llm_reasoning,
        });

        cards.push({
            label: "LAA",
            subLabel: "(Least Acceptable Agreement)",
            value: formatCurrency(entry.price_laa?.target_price) ?? "-",
            supplierBid: formatCurrency(entry.price_laa?.supplier_bid) ?? "-",
        });

        // Always show BATNA
        cards.push({
            label: "BATNA",
            subLabel: "(Best Alternative to a Negotiated Agreement)",
            value: entry.batna || "-",
            isBatna: true,
        });

        // Targets Allocation - hidden per request
        // const allocationLines = [
        //     `Supplier Volume Allocation: ${entry.target_allocation?.supplier_volume_allocation ?? "-"}`,
        //     `Opening Volume Allocation: ${entry.target_allocation?.opening_volume_allocation ?? "-"}`,
        // ];
        // cards.push({
        //     label: "Targets Allocation",
        //     subLabel: "",
        //     value: "",
        //     bullets: allocationLines,
        // });

        // Category goals - only show if available
        if (entry.category_goals) {
            let goals: string[] = [];
            if (Array.isArray(entry.category_goals)) {
                goals = entry.category_goals
                    .map((g) => g?.rationale || g?.objective || g?.parameter_name)
                    .filter(Boolean) as string[];
            } else if (typeof entry.category_goals === "string") {
                goals = [entry.category_goals];
            } else if (typeof entry.category_goals === "object") {
                goals = Object.values(entry.category_goals)
                    .filter((v) => typeof v === "string") as string[];
            }
            if (goals.length) {
                cards.push({
                    label: "Category Goals",
                    subLabel: "Additional Considerations",
                    value: "",
                    bullets: goals,
                });
            }
        }
        return cards;
    }, []);

    const buildSummaryFromStrategy = useCallback((productName: string, tenderSourcingProductId: number): SummaryEntry => {
        // Find the sourcing product by ID
        const sourcingProduct = strategy?.sourcingProducts?.find(sp => sp.tenderSourcingProductId === tenderSourcingProductId);

        if (!sourcingProduct) {
            return {
                product_name: productName,
                target_pricing: { opening_target: null, internal_target: null, supplier_target: null },
                batna: null,
            };
        }

        const target = sourcingProduct.target;
        const bid = sourcingProduct.bids?.[0]; // Get first bid
        const supplierBidPrice = bid?.price ?? target?.currentBidPrice ?? null;

        return {
            product_name: productName,
            target_pricing: {
                opening_target: target?.openingTarget ?? null,
                internal_target: target?.internalTarget ?? null,
                supplier_target: target?.supplierTarget ?? null,
            },
            mdo: target?.mdo !== undefined
                ? { target_price: target.mdo, supplier_bid: supplierBidPrice }
                : undefined,
            price_laa: target?.laa !== undefined
                ? { target_price: target.laa, supplier_bid: supplierBidPrice }
                : undefined,
            batna: target?.batna ?? null,
            target_allocation: {
                supplier_volume_allocation: target?.targetAllocation?.toString() ?? null,
                opening_volume_allocation: target?.openingAllocation?.toString() ?? null,
            },
            llm_reasoning: target?.reasoning?.llmReasoning ?? null,
        };
    }, [strategy]);

    // Populate bdTargets from GraphQL strategy.sourcingProducts
    useEffect(() => {
        if (!strategy?.sourcingProducts || strategy.sourcingProducts.length === 0) {
            setBdTargets([]);
            return;
        }

        // Build target headings from strategy.sourcingProducts
        const targetHeadings: TargetHeading[] = strategy.sourcingProducts.map(product => {
            const bid = product.bids?.[0]; // Get first bid if available

            return {
                bid_id: product.tenderSourcingProductId, // Use tenderSourcingProductId for lookup
                tender_id: product.tenderId,
                product_description: product.productDescription,
                tender_name: undefined, // tender name not available in new structure
                supplier_bid: bid?.price ?? null,
                currency: product.target?.currency ?? bid?.currency ?? '€',
                target_price_reasoning: product.target?.reasoning?.targetPriceReasoning,
            };
        });

        setBdTargets(targetHeadings);
    }, [strategy]);

    // Update navigation context when tender changes
    useEffect(() => {
        if (selectedTender) {
            setSelectedTenderId(selectedTender.tender_id);
            // Update URL when tender changes via context
            setTimeout(() => updateURL(location.pathname), 0);
        }
    }, [selectedTender, setSelectedTenderId, updateURL, location.pathname])

    useEffect(() => {


        // Cleanup function to reset selectedTender when component is unmounted
        return () => {
            setSelectedTender(null);
            stopStrategySubscription();
        };
    }, []);


    useEffect(() => {
        if (!negId || !selectedSupplier?.nsp_supplier_id) return;

        const supplierId = selectedSupplier.nsp_supplier_id;
        fetchStrategy(apolloClient, negId, supplierId, null);
    }, [negId, selectedSupplier, apolloClient, fetchStrategy]);

    // Fetch targets (negotiation levers) after strategy is loaded
    useEffect(() => {
        if (strategy && strategy.id) {
            fetchTargets(apolloClient, strategy.id);
        }
    }, [strategy, fetchTargets]);

    const summaryByProduct = useMemo(() => {
        const map = new Map<string, SummaryEntry>();
        (bdSummary || []).forEach((entry) => {
            const key = normalizeProductKey(entry.product_name);
            if (key) map.set(key, entry);
        });
        return map;
    }, [bdSummary]);

    const handleFetchSummary = useCallback(
        (productName: string, tenderSourcingProductId: number) => {
            // Use data from strategy.sourcingProducts
            setLoadingSummary(true);
            const summaryEntry = buildSummaryFromStrategy(productName, tenderSourcingProductId);
            setBdSummary((prev) => {
                const map = new Map<string, SummaryEntry>();
                prev.forEach((e) => {
                    const key = normalizeProductKey(e.product_name);
                    if (key) map.set(key, e);
                });
                const key = normalizeProductKey(summaryEntry.product_name);
                if (key) map.set(key, summaryEntry);
                return Array.from(map.values());
            });
            setLoadingSummary(false);
        },
        [buildSummaryFromStrategy]
    );

    // Fetch tactics (concessions) after targets are loaded
    useEffect(() => {
        if (negId && selectedSupplier && targets.length > 0) {
            const supplierId = selectedSupplier.nsp_supplier_id;
            fetchTactics(apolloClient, negId, supplierId);
        }
    }, [negId, selectedSupplier, selectedTender, targets, fetchTactics]);

    // Fetch alternatives after tactics are loaded
    useEffect(() => {
        if (strategy && strategy.id && tactics.length > 0) {
            fetchAlternatives(apolloClient, strategy.id);
        }
    }, [strategy, tactics, fetchAlternatives]);

    // Update KPIs when strategy changes
    useEffect(() => {
        if (strategy) {
            const updatedKpis: TabItem[] = [];

            // All values come from product-level targets (negotiation_strategy_targets)
            const firstProduct = strategy.sourcingProducts?.[0];
            const target = firstProduct?.target;

            // Supplier bid is stored in target.currentBidPrice
            const supplierBid = target?.currentBidPrice;
            const mdo = target?.mdo;
            const laa = target?.laa;
            const batna = target?.batna;

            // Always show Buyer Targets section
            updatedKpis.push({
                primaryText: "Buyer Targets",
                key: "Target Price",
                value: mdo ? `€ ${mdo.toFixed(2)}` : "-",
                key2: "Supplier Bid",
                value2: supplierBid ? `€ ${supplierBid.toFixed(2)}` : "-",
            });

            // Always show MDO
            updatedKpis.push({
                primaryText: "MDO",
                key: "(Most Desired Outcome)",
                value: mdo ? `€ ${mdo.toFixed(2)}` : "-",
                key2: "",
                value2: "",
            });

            // Always show LAA
            updatedKpis.push({
                primaryText: "LAA",
                key: "(Least Acceptable Agreement)",
                value: laa ? `€ ${laa.toFixed(2)}` : "-",
                key2: "",
                value2: "",
            });

            // BATNA is also at product level (negotiation_strategy_targets)
            updatedKpis.push({
                primaryText: "BATNA",
                key: "(Best Alternative to a Negotiated Agreement)",
                value: batna || "-",
                key2: "",
                value2: "",
            });

            setKpis(updatedKpis);
        } else {
            setKpis([]);
        }
    }, [strategy]);

    // Initialize tender and supplier from URL state, or set defaults
    useEffect(() => {
        // Initialize tender from URL state if available
        if (selectedTenderId && tenders.length > 0 && !selectedTender) {
            const tender = tenders.find(t => t.tender_id === selectedTenderId);
            if (tender) {
                setSelectedTender(tender);
            }
        }
    }, [selectedTenderId, tenders, selectedTender, setSelectedTender]);

    // Fallback: default to first tender when none is selected so that supplier/API flows trigger on mount
    useEffect(() => {
        if (!selectedTender && tenders.length > 0) {
            const firstTender = tenders[0];
            setSelectedTender(firstTender);
            setSelectedTenderId(firstTender.tender_id);
        }
    }, [selectedTender, tenders, setSelectedTender, setSelectedTenderId]);

    // Initialize supplier from URL state, or set defaults (only runs when suppliers load)
    useEffect(() => {
        if (suppliers.length === 0 || selectedSupplier) return;

        if (selectedSupplierId) {
            const supplier = suppliers.find(s => s.nsp_supplier_id === selectedSupplierId);
            if (supplier) {
                setSelectedSupplier(supplier);
                return;
            }
        }

        if (supplierTabIndex !== null && supplierTabIndex < suppliers.length) {
            setSelectedSupplier(suppliers[supplierTabIndex]);
            return;
        }

        setSelectedSupplier(suppliers[0]);
    }, [suppliers]);

    // Update navigation context when supplier changes
    useEffect(() => {
        if (selectedSupplier) {
            const supplierId = selectedSupplier.nsp_supplier_id;
            const supplierIndex = suppliers.findIndex(s => s.nsp_supplier_id === supplierId);
            setSelectedSupplierId(supplierId);
            setSupplierTabIndex(supplierIndex >= 0 ? supplierIndex : null);

            // Update URL with new supplier selection
            setTimeout(() => updateURL(location.pathname), 0);
        }
    }, [selectedSupplier, suppliers, setSelectedSupplierId, setSupplierTabIndex, updateURL, location.pathname]);

    // Modify welcome message only when chat is first opened on this screen
    useEffect(() => {
        if (isOpen && !hasModifiedWelcome && selectedSupplier) {
            // Use a timeout to ensure messages are loaded first
            const timer = setTimeout(() => {
                const currentMessages = useChatStore.getState().messages;

                // Find the welcome message (should be the first AI message)
                const welcomeMessage = currentMessages.find(msg =>
                    msg.sender === 'ai' &&
                    msg.id.startsWith('welcome-')
                );

                if (welcomeMessage) {
                    // Replace the welcome message with our custom one including suggested questions
                    const updatedMessages = currentMessages.map(msg =>
                        msg.id === welcomeMessage.id
                            ? {
                                ...msg,
                                text: `Ready to analyze negotiation strategy for ${selectedSupplier.supplier_name}. Click the suggestion below to start the comprehensive targets & strategies analysis.`,
                                suggestedQuestions: ["Start Targets & Strategies"]
                            }
                            : msg
                    );

                    // Update the messages in the store
                    useChatStore.setState({ messages: updatedMessages });
                    setHasModifiedWelcome(true);
                }
            }, 100);

            return () => clearTimeout(timer);
        }

        // Reset the flag when chat is closed
        if (!isOpen) {
            setHasModifiedWelcome(false);
        }
    }, [isOpen, hasModifiedWelcome, selectedSupplier]);
    useEffect(() => {
        if (negId && selectedSupplierId) {
            // tender_id is optional - pass it if available
            getScriptStatus(negId, selectedSupplierId, selectedTender?.tender_id);
        }
    }, [negId, selectedSupplierId, selectedTender?.tender_id])

    // Show error state if there's an error fetching suppliers
    if (suppliersError) {
        return <div>Error loading suppliers: {suppliersError}</div>;
    }

    // Use suppliers from API


    const handleRegenerateScript = async () => {
        const payload = {
            "negotiation_id": negId,
            "supplier_id": selectedSupplierId,
            "regenerate": !scriptStatus?.exists   // If script exists, don't regenerate
        }
        await regenerateScript(payload)
        setIsRegenerated(true)
        await getScriptStatus(negId, selectedSupplierId)
    }


    return (
        <div className="simple-tabs">
            {/* Supplier Dropdown */}
            <div>
                <Card style={{
                    backgroundColor: "white",
                    border: "1px solid var(--border-muted)"
                }}>
                    <div className="field">
                        <label className="block font-bold mb-2" style={{ color: "black" }}>
                            Select Supplier:
                        </label>
                        <CustomDropdown
                            selectedOption={selectedSupplierOption}
                            options={supplierOptions}
                            placeholder="Select supplier..."
                            onSelect={(opt) => {
                                const sup = suppliers.find((s) => s.nsp_supplier_id?.toString() === opt?.code);
                                if (sup) {
                                    setSelectedSupplier(sup);
                                    setSupplierTabIndex(suppliers.findIndex((s) => s.nsp_supplier_id === sup.nsp_supplier_id));
                                }
                            }}
                        />
                        {!suppliers.length && (
                            <small className="text-gray-500">No suppliers available for this negotiation</small>
                        )}
                    </div>
                </Card>
            </div>

            {/* header */}
            <div className="main-wrapper">
                {/* <div className="header">
                    <span className="header-text">Targets & Strategies</span>
                </div> */}

                {/* targets */}
                <div className="briefing-doc__targets">
                    <div className="briefing-doc__text briefing-doc__targets-heading" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div className="briefing-doc__section-heading">Targets</div>
                        <div className="briefing-doc__group-toggle">
                            <span>Group by Display</span>
                            <InputSwitch
                                checked={groupByDisplay}
                                onChange={(e) => setGroupByDisplay(e.value)}
                            />
                        </div>
                    </div>
                    <div className="briefing-doc__target-accordions">
                        {loadingTargets && <div className="briefing-doc__muted">Loading targets…</div>}
                        {loadingSummary && <div className="briefing-doc__muted">Loading target summary…</div>}

                        {/* GROUPED VIEW */}
                        {groupByDisplay && strategy?.displayGroups && (
                            <>
                                {strategy.displayGroups.length === 0 && (
                                    <div className="briefing-doc__muted">No display groups available for this supplier.</div>
                                )}
                                {strategy.displayGroups.map((group) => (
                                    <DisplayGroupAccordion
                                        key={group.displayId}
                                        group={group}
                                        onVariantInfoClick={(reasoning) => {
                                            setModalContent({ llmReasoning: reasoning });
                                            setShowDetailsModal(true);
                                        }}
                                    />
                                ))}
                            </>
                        )}

                        {/* FLAT VIEW - Render targets from sourcingProducts */}
                        {!groupByDisplay && (
                            <>
                                {!loadingTargets && bdTargets.length === 0 && (
                                    <div className="briefing-doc__muted">No targets available for this supplier.</div>
                                )}
                        {bdTargets.map((target) => {
                            const productKey = normalizeProductKey(target.product_description);
                            const summaryEntry = summaryByProduct.get(productKey);
                            const cards = summaryEntry ? buildSummaryCards(summaryEntry) : [];
                            const tenderLabel = target.tender_name ? ` (${target.tender_name})` : "";
                            return (
                                <TargetAccordion
                                    key={`${target.product_description}-${target.bid_id}`}
                                    title={`${target.product_description}${tenderLabel}`}
                                    targetCards={cards}
                                    defaultOpen={false}
                                    onOpen={() => handleFetchSummary(target.product_description, target.bid_id)}
                                    goalsLoading={loadingSummary}
                                    dataFetched={cards.length > 0}
                                    targetPriceReasoning={target.target_price_reasoning}
                                    onInfoClick={() => {
                                        setModalContent({
                                            targetPriceReasoning: target.target_price_reasoning
                                        });
                                        setShowDetailsModal(true);
                                    }}
                                    onReasoningClick={(reasoning) => {
                                        setModalContent({ llmReasoning: reasoning });
                                        setShowDetailsModal(true);
                                    }}
                                />
                            );
                        })}
                            </>
                        )}
                    </div>
                </div>

                {/* dynamics and approach */}
                <div className="negotiations-wrapper">
                    {/* dynamics */}
                    <div className="inner-wrapper">
                        <div className="header">
                            <p className="m-0">Power Dynamics</p>
                            {(strategy?.powerBalanceReasoning || strategy?.powerDynamicsAssessment?.length) && (
                                <Button
                                    icon="pi pi-info-circle"
                                    className="p-button-rounded p-button-text p-button-sm"
                                    style={{
                                        color: 'var(--text-muted)',
                                        marginLeft: '5px',
                                        padding: '0.5rem'
                                    }}
                                    onClick={() => {
                                        setModalContent({
                                            targetPriceReasoning: strategy?.powerBalanceReasoning,
                                            powerDynamicsAssessment: strategy?.powerDynamicsAssessment,
                                            buyerPowerScore: strategy?.buyerPowerScore,
                                            supplierPowerScore: strategy?.supplierPowerScore,
                                            powerDifferential: strategy?.powerDifferential
                                        });
                                        setShowDetailsModal(true);
                                    }}
                                    tooltip="View power dynamics details"
                                    tooltipOptions={{ position: 'top' }}
                                />
                            )}
                        </div>
                        <div className="table">
                            <ReactMarkdown>
                                {strategy?.powerBalance || "-"}
                            </ReactMarkdown>
                        </div>
                    </div>

                    {/* approach */}
                    <div className="inner-wrapper">
                        <div className="header">
                            <p className="m-0">Approach</p>
                            {strategy?.negotiationApproach && (
                                <Button
                                    icon="pi pi-info-circle"
                                    className="p-button-rounded p-button-text p-button-sm"
                                    style={{
                                        color: 'var(--text-muted)',
                                        marginLeft: '5px',
                                        padding: '0.5rem'
                                    }}
                                    onClick={() => {
                                        setModalContent({
                                            targetPriceReasoning: strategy.negotiationApproach
                                        });
                                        setShowDetailsModal(true);
                                    }}
                                    tooltip="View full approach details"
                                    tooltipOptions={{ position: 'top' }}
                                />
                            )}
                        </div>
                        <div className="table">
                            <ReactMarkdown>
                                {strategy?.approachSummary || strategy?.negotiationApproach || "-"}
                            </ReactMarkdown>
                        </div>
                    </div>
                </div>

                {/* negotiations and concessions */}
                <div className="negotiations-wrapper">
                    <div className="inner-wrapper">
                        <div className="header">
                            <p className="m-0">Negotiation Levers</p>
                            {/* <div>
										<Button
											icon={<EditIcon />}
											rounded
											text
											aria-label="Filter"
										/>
										<Button
											icon={<HistoryIcon />}
											rounded
											text
											aria-label="Filter"
										/>
									</div> */}
                        </div>
                        <div className="table">
                            {targets.length > 0 ? (
                                <NegotiationTable
                                    headers={headers}
                                    data={targets.map(t => ({
                                        parameter: formatParameterName(t.parameterName),
                                        objective: (
                                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                {t.objective}
                                            </ReactMarkdown>
                                        ),
                                        impact: t.leverageLevel === 'high' ? 'High' : t.leverageLevel === 'medium' ? 'Medium' : 'Low',
                                        info: t.rationale ? (
                                            <Button
                                                icon="pi pi-info-circle"
                                                className="p-button-rounded p-button-text p-button-sm"
                                                style={{
                                                    color: 'var(--text-muted)',
                                                    padding: '0.25rem'
                                                }}
                                                onClick={() => {
                                                    setModalContent({
                                                        targetPriceReasoning: t.rationale
                                                    });
                                                    setShowDetailsModal(true);
                                                }}
                                                tooltip="View rationale"
                                                tooltipOptions={{ position: 'top' }}
                                            />
                                        ) : null
                                    }))}
                                />
                            ) : (
                                <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                                    No negotiation targets available
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="inner-wrapper">
                        <div className="header">
                            <p className="m-0">Concessions</p>
                            {/* <div>
										<Button
											icon={<EditIcon />}
											rounded
											text
											aria-label="Filter"
										/>
										<Button
											icon={<HistoryIcon />}
											rounded
											text
											aria-label="Filter"
										/>
									</div> */}
                        </div>
                        <div className="table">
                            {tactics.length > 0 ? (
                                <NegotiationTable
                                    headers={concessionHeaders}
                                    data={tactics.map(t => ({
                                        parameter: t.parameterName,
                                        objective: t.maximumConcession
                                    }))}
                                />
                            ) : (
                                <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                                    No concession tactics available
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Button to Create / View Script */}
            <CustomButton
                label={scriptStatus?.exists ? "View Script" : "Create Script"}
                onClick={handleRegenerateScript}
                iconsOnly={false}
                loadingProp={createScriptLoading}
                variant="outlined"
                icon="pi pi-file-pdf"
                className="float-right align-left"
            />

            {/* Modal for displaying details with markdown */}
            <Dialog
                header="Rationale"
                visible={showDetailsModal}
                onHide={() => setShowDetailsModal(false)}
                style={{ width: '50vw' }}
                breakpoints={{ '960px': '75vw', '640px': '95vw' }}
                modal
                dismissableMask
            >
                <div style={{
                    maxHeight: '70vh',
                    overflowY: 'auto',
                    padding: '1rem'
                }}>
                    {/* Power Dynamics Summary Stats */}
                    {(modalContent.buyerPowerScore !== undefined || modalContent.supplierPowerScore !== undefined) && (
                        <div style={{ marginBottom: '1.5rem' }}>
                            <h4 style={{
                                fontWeight: '600',
                                color: 'var(--text-primary)',
                                marginBottom: '0.75rem',
                                fontSize: '1.125rem'
                            }}>
                                Power Assessment Summary
                            </h4>
                            <table style={{
                                width: '100%',
                                borderCollapse: 'collapse',
                                fontSize: '0.875rem',
                                marginBottom: '1rem'
                            }}>
                                <tbody>
                                    <tr style={{ borderBottom: '1px solid var(--border-muted)' }}>
                                        <td style={{ padding: '0.75rem', fontWeight: '600', color: 'var(--brand-secondary)' }}>Buyer Score</td>
                                        <td style={{ padding: '0.75rem', textAlign: 'right' }}>{modalContent.buyerPowerScore ?? "-"}</td>
                                    </tr>
                                    <tr style={{ borderBottom: '1px solid var(--border-muted)' }}>
                                        <td style={{ padding: '0.75rem', fontWeight: '600', color: 'var(--status-error)' }}>Supplier Score</td>
                                        <td style={{ padding: '0.75rem', textAlign: 'right' }}>{modalContent.supplierPowerScore ?? "-"}</td>
                                    </tr>
                                    <tr style={{ backgroundColor: 'var(--surface-background)' }}>
                                        <td style={{ padding: '0.75rem', fontWeight: '600' }}>Differential</td>
                                        <td style={{
                                            padding: '0.75rem',
                                            textAlign: 'right',
                                            fontWeight: '700',
                                            color: modalContent.powerDifferential !== undefined
                                                ? (modalContent.powerDifferential > 0 ? 'var(--status-success)' : modalContent.powerDifferential < 0 ? 'var(--status-error)' : 'var(--text-muted)')
                                                : 'var(--text-muted)'
                                        }}>
                                            {modalContent.powerDifferential !== undefined
                                                ? (modalContent.powerDifferential > 0 ? `+${modalContent.powerDifferential}` : modalContent.powerDifferential)
                                                : "-"}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Reasoning Text */}
                    {modalContent.targetPriceReasoning && (
                        <div style={{ marginBottom: '1.5rem' }}>
                            <h4 style={{
                                fontWeight: '600',
                                color: 'var(--text-primary)',
                                marginBottom: '0.75rem',
                                fontSize: '1.125rem'
                            }}>
                                Rationale
                            </h4>
                            <div className="markdown-content" style={{
                                fontSize: '0.875rem',
                                lineHeight: '1.5',
                                color: 'var(--text-muted)'
                            }}>
                                <ReactMarkdown
                                    remarkPlugins={[remarkGfm]}
                                    components={{
                                        p: ({ children }) => <p style={{ marginBottom: '0.75rem' }}>{children}</p>,
                                        ul: ({ children }) => <ul style={{ marginLeft: '1.25rem', marginBottom: '0.75rem', listStyleType: 'disc' }}>{children}</ul>,
                                        ol: ({ children }) => <ol style={{ marginLeft: '1.25rem', marginBottom: '0.75rem', listStyleType: 'decimal' }}>{children}</ol>,
                                        li: ({ children }) => <li style={{ marginBottom: '0.25rem' }}>{children}</li>,
                                        strong: ({ children }) => <strong style={{ fontWeight: '600' }}>{children}</strong>,
                                        em: ({ children }) => <em style={{ fontStyle: 'italic' }}>{children}</em>,
                                        code: ({ children }) => <code style={{ backgroundColor: 'var(--surface-variant)', padding: '0.125rem 0.25rem', borderRadius: '0.25rem', fontSize: '0.875em' }}>{children}</code>,
                                        pre: ({ children }) => <pre style={{ backgroundColor: 'var(--surface-variant)', padding: '0.75rem', borderRadius: '0.375rem', overflow: 'auto', marginBottom: '0.75rem' }}>{children}</pre>,
                                        blockquote: ({ children }) => <blockquote style={{ borderLeft: '4px solid var(--border-muted)', paddingLeft: '1rem', marginLeft: 0, marginBottom: '0.75rem', fontStyle: 'italic' }}>{children}</blockquote>,
                                        h1: ({ children }) => <h1 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '0.75rem', marginTop: '1rem' }}>{children}</h1>,
                                        h2: ({ children }) => <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem', marginTop: '0.75rem' }}>{children}</h2>,
                                        h3: ({ children }) => <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '0.5rem', marginTop: '0.75rem' }}>{children}</h3>,
                                    }}
                                >
                                    {modalContent.targetPriceReasoning}
                                </ReactMarkdown>
                            </div>
                        </div>
                    )}

                    {/* LLM Reasoning Text */}
                    {modalContent.llmReasoning && (
                        <div style={{ marginBottom: '1.5rem' }}>
                            <h4 style={{
                                fontWeight: '600',
                                color: 'var(--text-primary)',
                                marginBottom: '0.75rem',
                                fontSize: '1.125rem'
                            }}>
                                Target Price Reasoning
                            </h4>
                            <div className="markdown-content" style={{
                                fontSize: '0.875rem',
                                lineHeight: '1.5',
                                color: 'var(--text-muted)'
                            }}>
                                <ReactMarkdown
                                    remarkPlugins={[remarkGfm]}
                                    components={{
                                        p: ({ children }) => <p style={{ marginBottom: '0.75rem' }}>{children}</p>,
                                        ul: ({ children }) => <ul style={{ marginLeft: '1.25rem', marginBottom: '0.75rem', listStyleType: 'disc' }}>{children}</ul>,
                                        ol: ({ children }) => <ol style={{ marginLeft: '1.25rem', marginBottom: '0.75rem', listStyleType: 'decimal' }}>{children}</ol>,
                                        li: ({ children }) => <li style={{ marginBottom: '0.25rem' }}>{children}</li>,
                                        strong: ({ children }) => <strong style={{ fontWeight: '600' }}>{children}</strong>,
                                        em: ({ children }) => <em style={{ fontStyle: 'italic' }}>{children}</em>,
                                        code: ({ children }) => <code style={{ backgroundColor: 'var(--surface-variant)', padding: '0.125rem 0.25rem', borderRadius: '0.25rem', fontSize: '0.875em' }}>{children}</code>,
                                        pre: ({ children }) => <pre style={{ backgroundColor: 'var(--surface-variant)', padding: '0.75rem', borderRadius: '0.375rem', overflow: 'auto', marginBottom: '0.75rem' }}>{children}</pre>,
                                        blockquote: ({ children }) => <blockquote style={{ borderLeft: '4px solid var(--border-muted)', paddingLeft: '1rem', marginLeft: 0, marginBottom: '0.75rem', fontStyle: 'italic' }}>{children}</blockquote>,
                                        h1: ({ children }) => <h1 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '0.75rem', marginTop: '1rem' }}>{children}</h1>,
                                        h2: ({ children }) => <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem', marginTop: '0.75rem' }}>{children}</h2>,
                                        h3: ({ children }) => <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '0.5rem', marginTop: '0.75rem' }}>{children}</h3>,
                                    }}
                                >
                                    {modalContent.llmReasoning}
                                </ReactMarkdown>
                            </div>
                        </div>
                    )}

                    {/* Power Dynamics Assessment Q&A Table */}
                    {modalContent.powerDynamicsAssessment && modalContent.powerDynamicsAssessment.length > 0 && (
                        <div>
                            <h4 style={{
                                fontWeight: '600',
                                color: 'var(--text-primary)',
                                marginBottom: '0.75rem',
                                fontSize: '1.125rem'
                            }}>
                                Assessment Details
                            </h4>

                            {/* Buyer's Assessment */}
                            {modalContent.powerDynamicsAssessment.filter(item => item.party === 'buyer').length > 0 && (
                                <div style={{ marginBottom: '1rem' }}>
                                    <h5 style={{
                                        fontWeight: '600',
                                        color: 'var(--brand-secondary)',
                                        marginBottom: '0.5rem',
                                        fontSize: '0.875rem'
                                    }}>
                                        Buyer's Power Position
                                    </h5>
                                    <table style={{
                                        width: '100%',
                                        borderCollapse: 'collapse',
                                        fontSize: '0.8125rem',
                                        border: '1px solid var(--border-muted)'
                                    }}>
                                        <thead>
                                            <tr style={{ backgroundColor: 'var(--color-blue-50)' }}>
                                                <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid var(--border-muted)', width: '35%' }}>Question</th>
                                                <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid var(--border-muted)', width: '15%' }}>Answer</th>
                                                <th style={{ padding: '0.5rem', textAlign: 'center', borderBottom: '1px solid var(--border-muted)', width: '10%' }}>Score</th>
                                                <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid var(--border-muted)', width: '40%' }}>Reasoning</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {modalContent.powerDynamicsAssessment.filter(item => item.party === 'buyer').map(item => (
                                                <tr key={item.id} style={{ borderBottom: '1px solid var(--border-muted)' }}>
                                                    <td style={{ padding: '0.5rem', verticalAlign: 'top' }}>{item.question}</td>
                                                    <td style={{ padding: '0.5rem', verticalAlign: 'top' }}>{item.answer || "-"}</td>
                                                    <td style={{
                                                        padding: '0.5rem',
                                                        textAlign: 'center',
                                                        verticalAlign: 'top',
                                                        fontWeight: '600',
                                                        color: item.score !== undefined ? (item.score >= 4 ? 'var(--status-success)' : item.score >= 2 ? 'var(--status-warning)' : 'var(--status-error)') : 'var(--text-muted)'
                                                    }}>
                                                        {item.score ?? "-"}
                                                    </td>
                                                    <td style={{ padding: '0.5rem', verticalAlign: 'top', color: 'var(--text-muted)' }}>{item.reasoning || "-"}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* Supplier's Assessment */}
                            {modalContent.powerDynamicsAssessment.filter(item => item.party === 'supplier').length > 0 && (
                                <div>
                                    <h5 style={{
                                        fontWeight: '600',
                                        color: 'var(--status-error)',
                                        marginBottom: '0.5rem',
                                        fontSize: '0.875rem'
                                    }}>
                                        Supplier's Power Position
                                    </h5>
                                    <table style={{
                                        width: '100%',
                                        borderCollapse: 'collapse',
                                        fontSize: '0.8125rem',
                                        border: '1px solid var(--border-muted)'
                                    }}>
                                        <thead>
                                            <tr style={{ backgroundColor: 'var(--color-red-50)' }}>
                                                <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid var(--border-muted)', width: '35%' }}>Question</th>
                                                <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid var(--border-muted)', width: '15%' }}>Answer</th>
                                                <th style={{ padding: '0.5rem', textAlign: 'center', borderBottom: '1px solid var(--border-muted)', width: '10%' }}>Score</th>
                                                <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid var(--border-muted)', width: '40%' }}>Reasoning</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {modalContent.powerDynamicsAssessment.filter(item => item.party === 'supplier').map(item => (
                                                <tr key={item.id} style={{ borderBottom: '1px solid var(--border-muted)' }}>
                                                    <td style={{ padding: '0.5rem', verticalAlign: 'top' }}>{item.question}</td>
                                                    <td style={{ padding: '0.5rem', verticalAlign: 'top' }}>{item.answer || "-"}</td>
                                                    <td style={{
                                                        padding: '0.5rem',
                                                        textAlign: 'center',
                                                        verticalAlign: 'top',
                                                        fontWeight: '600',
                                                        color: item.score !== undefined ? (item.score >= 4 ? 'var(--status-success)' : item.score >= 2 ? 'var(--status-warning)' : 'var(--status-error)') : 'var(--text-muted)'
                                                    }}>
                                                        {item.score ?? "-"}
                                                    </td>
                                                    <td style={{ padding: '0.5rem', verticalAlign: 'top', color: 'var(--text-muted)' }}>{item.reasoning || "-"}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                </div>
            </Dialog>

            {/* Regenerated Targets and Tactics */}
            <RegeneratedTargets
                isRegenerated={isRegenerated}
                setIsRegenerated={setIsRegenerated}
                data={regeneratedTableData}
            />
        </div>
    );
};

export default TargetsTactics;

type TargetAccordionProps = {
    title: string;
    targetCards: TargetCard[];
    defaultOpen?: boolean;
    onOpen?: () => void;
    goalsLoading?: boolean;
    dataFetched?: boolean;
    targetPriceReasoning?: string;
    onInfoClick?: () => void;
    onReasoningClick?: (reasoning: string) => void;
};

const TargetAccordion = ({
    title,
    targetCards,
    defaultOpen = false,
    onOpen,
    goalsLoading = false,
    dataFetched = false,
    targetPriceReasoning,
    onInfoClick,
    onReasoningClick,
}: TargetAccordionProps) => {
    const [open, setOpen] = useState(defaultOpen);

    return (
        <div className={`briefing-doc__target-accordion ${open ? "is-open" : ""}`}>
            <button
                type="button"
                className="briefing-doc__target-toggle"
                onClick={() =>
                    setOpen((prev) => {
                        const next = !prev;
                        if (next && onOpen) {
                            onOpen();
                        }
                        return next;
                    })
                }
            >
                <span>{title}</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {targetPriceReasoning && (
                        <i
                            className="pi pi-eye"
                            style={{
                                color: 'var(--text-muted)',
                                padding: '0.25rem',
                                cursor: 'pointer',
                                fontSize: '1rem'
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (onInfoClick) onInfoClick();
                            }}
                            title="View target price reasoning"
                        />
                    )}
                    <i className={`pi ${open ? "pi-chevron-up" : "pi-chevron-down"}`} />
                </span>
            </button>
            {open && (
                <div className="briefing-doc__target-cards">
                    {targetCards.length === 0 && (
                        <div className="briefing-doc__muted">
                            {goalsLoading
                                ? "Loading target summary…"
                                : dataFetched
                                    ? "No target data available."
                                    : "Expand to load target summary."}
                        </div>
                    )}
                    {targetCards.map((c) => {
                        // Target Pricing hidden per request
                        if (c.type === "target_pricing") {
                            return null;
                        }

                        return (
                            <div key={c.label}>
                                <div className="briefing-doc__card-heading" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    {c.label}
                                    {c.reasoning && onReasoningClick && (
                                        <i
                                            className="pi pi-info-circle"
                                            style={{
                                                color: 'var(--text-muted)',
                                                cursor: 'pointer',
                                                fontSize: '0.875rem'
                                            }}
                                            onClick={() => onReasoningClick(c.reasoning!)}
                                            title="View reasoning"
                                        />
                                    )}
                                </div>
                                <div className="briefing-doc__card-subheading">{c.subLabel}</div>
                                <div className={`briefing-doc__card-value${c.isBatna ? " briefing-doc__card-value-batna" : ""}`}>
                                    {c.value ?? ""}
                                </div>
                                {c.bullets && c.bullets.length > 0 && (
                                    <ul className="briefing-doc__card-list">
                                        {c.bullets.map((b: string) => (
                                            <li key={b}>{b}</li>
                                        ))}
                                    </ul>
                                )}
                                {c.supplierBid && (
                                    <div className="briefing-doc__card-supplier">
                                        <span className="briefing-doc__card-supplier-label">Supplier Bid</span>
                                        <span className="briefing-doc__card-supplier-value">{c.supplierBid}</span>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
